import { useOrganizationStore } from '@/stores/organizationStore';
import { chain, find } from 'lodash';
import { MissingDataError, RedirectError } from './errors';
import { canCreateOrganizations } from '@/utils/helpers';
import { LoaderFunctionArgs } from 'react-router-dom';
import { UserRole } from '@/types/types';
import {
  Action,
  OrganizationResource,
  OrganizationResourceData,
  WorkspacePolicyData,
  WorkspaceResource,
  WorkspaceResourceData,
} from '@/auth/types';
import { hasPermission, hasPermissionWithPolicy } from '@/auth/hasPermission';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { getOrganizationConstraints, getWorkspaceConstraints } from '@/auth/utils';
import { useWorkspaceIdStore } from '@/stores/workspaceIdStore';
import { getInvitationQueryString } from '@/utils/invitationUrlParams';

export async function isUserLoggedInGuard(args: LoaderFunctionArgs) {
  const userId = useUserPermissionsStore.getState().userId;
  const email = useUserPermissionsStore.getState().email;
  const url = new URL(args.request.url);

  const isUserLoggedIn = !!userId && !!email;
  if (
    !isUserLoggedIn &&
    (url.pathname === '/handleInvitation' || url.pathname === '/subscriptions')
  ) {
    const redirectUrl = new URL('/login', window.location.origin);
    redirectUrl.searchParams.set('redirectUrl', `${url.pathname}${url.search}`);
    throw new RedirectError('User is not authenticated', redirectUrl.toString());
  } else if (!isUserLoggedIn && url.pathname !== '/login') {
    throw new RedirectError('User is not authenticated', '/login');
  } else if (isUserLoggedIn && url.pathname === '/login') {
    throw new RedirectError('User is already authenticated', '/');
  }
}

export async function isEmailVerifiedGuard(args: LoaderFunctionArgs) {
  const isEmailVerified = useUserPermissionsStore.getState().isEmailVerified;
  const userId = useUserPermissionsStore.getState().userId;
  const email = useUserPermissionsStore.getState().email;

  const isUserLoggedIn = !!userId && !!email;

  if (!isUserLoggedIn) return;

  const url = new URL(args.request.url);
  if (!isEmailVerified && url.pathname !== '/verify-email') {
    const invitationQs = getInvitationQueryString(url.searchParams);
    const verifyTarget = invitationQs ? `/verify-email?${invitationQs}` : '/verify-email';
    throw new RedirectError('Email is not verified', verifyTarget);
  }
}

export function hasAccess<
  L extends 'workspace' | 'organization',
  T extends L extends 'workspace' ? WorkspaceResourceData : OrganizationResourceData,
  R extends keyof T,
>(policyLevel: L, resource: R, action: Action) {
  return async function (args: LoaderFunctionArgs) {
    if (policyLevel === 'organization') {
      const organizationConstraints = getOrganizationConstraints();

      const isAccessGranted = hasPermission<
        'organization',
        OrganizationResourceData,
        keyof OrganizationResourceData,
        Action
      >(policyLevel, resource as OrganizationResource, action, organizationConstraints);
      if (!isAccessGranted) {
        throw new RedirectError(
          'User does not have proper organization permissions',
          '/',
        );
      }
    } else {
      const url = new URL(args.request.url);
      const workspaceId =
        url.searchParams.get('workspaceId') || useWorkspaceIdStore.getState().workspaceId;
      if (!workspaceId) {
        throw new RedirectError('Workspace id is not provided', '/');
      }
      const selectedWorkspaces =
        useOrganizationStore.getState().selectedOrganization?.workspaces;
      if (!selectedWorkspaces) {
        throw new MissingDataError('Selected organization workspaces are not set');
      }
      const selectedWorkspace = selectedWorkspaces.find(
        (workspace) => workspace.id === workspaceId,
      );
      if (!selectedWorkspace) {
        throw new MissingDataError('Selected workspace is not set');
      }
      const workspaceConstraints = getWorkspaceConstraints(url.pathname);
      const workspaceRole = useUserPermissionsStore.getState().workspacePermission
        ?.role as UserRole;
      if (!workspaceRole) {
        throw new MissingDataError('Workspace role is not set');
      }
      const workspacePolicyData: WorkspacePolicyData = {
        selectedWorkspace: selectedWorkspace,
        workspaceRole,
      };
      const isAccessGranted = hasPermissionWithPolicy<
        'workspace',
        WorkspaceResourceData,
        keyof WorkspaceResourceData,
        Action
      >(
        workspacePolicyData,
        selectedWorkspace.permissions,
        policyLevel,
        resource as WorkspaceResource,
        action,
        workspaceConstraints,
      );
      if (!isAccessGranted) {
        const redirectPath =
          window.location.pathname === '/'
            ? `organization-settings?workspaceId=${workspaceId}`
            : '/';
        throw new RedirectError(
          'User does not have proper workspace permissions',
          redirectPath,
        );
      }
    }
  };
}

export async function isWorkspaceMatchingOrganization(args: LoaderFunctionArgs) {
  const url = new URL(args.request.url);
  const workspaceId = url.searchParams.get('workspaceId');
  let organizationId = url.searchParams.get('organizationId');
  if (organizationId) {
    if (useOrganizationStore.getState().selectedOrganization?.id === organizationId)
      return;
    const organization = find(
      useOrganizationStore.getState().organizations,
      (organization) => organization.id === organizationId,
    );
    if (!organization) {
      throw new Error('Organization not found');
    }
    useOrganizationStore.getState().setSelectedOrganization(organization);
    const url = new URL(args.request.url);
    url.searchParams.delete('organizationId');
    throw new RedirectError(
      'Changed selected organization, refreshing data',
      url.toString(),
    );
  }
  const selectedOrganization = useOrganizationStore.getState().selectedOrganization;

  if (!selectedOrganization || !workspaceId) return;
  const isWorkspaceInOrganization = find(
    selectedOrganization?.workspaces,
    (workspace) => workspace.id === workspaceId,
  );
  if (isWorkspaceInOrganization) return;
  const isWorkspaceIdinWorkspaces = chain(useOrganizationStore.getState().organizations)
    .map('workspaces')
    .flatten()
    .find((workspace) => workspace.id === workspaceId)
    .value();
  if (!isWorkspaceIdinWorkspaces) {
    throw new Error('Workspace is not in any organization');
  }
  organizationId = isWorkspaceIdinWorkspaces?.organizationId;
  const organization = find(
    useOrganizationStore.getState().organizations,
    (organization) => organization.id === organizationId,
  );
  if (!organization) {
    throw new Error('Organization is not in any organization');
  }
  useOrganizationStore.getState().setSelectedOrganization(organization);
  throw new RedirectError(
    'Changed selected organization, refreshing data',
    args.request.url,
  );
}

export async function isUserAbleToCreateOrganizations() {
  const canUserCreateOrganizations = canCreateOrganizations();
  if (!canUserCreateOrganizations) {
    throw new RedirectError('User is not allowed to create organizations', '/');
  }
}

export async function isNoSubscription() {
  const organization = useOrganizationStore.getState().selectedOrganization;
  const subscriptionTier = organization?.subscriptionTier;
  if (!!subscriptionTier && subscriptionTier !== 'basic') {
    throw new RedirectError('This organization already has an active subscription', '/');
  }
}
